import type { ExportConfig, GeneratedFile, LanguageGenerator } from './types';
import { generateEnv, toSnake } from './utils';

function genConfig(config: ExportConfig): string {
  const appEntries = config.apps
    .map((a) => `    ${a.key}: { from_email: '${a.fromEmail}', key_env: '${a.envVarName}' }`)
    .join(",\n");

  const templateEntries = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const doc = t.schema
          ? `    # payload: ${t.schema.map((f) => f.key + (f.required ? '' : '?')).join(', ')}\n`
          : '';
        return `${doc}    ${t.camelName}: { app: :${a.key}, slug: '${t.slug}' }`;
      }),
    )
    .join(",\n\n");

  return `# mail_config.rb — generated by Mail Service SDK Export

MAIL_APPS = {
${appEntries}
}.freeze

MAIL_TEMPLATES = {
${templateEntries}
}.freeze
`;
}

function genService(config: ExportConfig): string {
  const typedMethods = config.apps
    .flatMap((a) =>
      a.templates.map((t) => {
        const fnName = `send_${toSnake(t.camelName)}`;
        if (t.schema && t.schema.length > 0) {
          const kwArgs = t.schema
            .map((f) => `${f.key}:${f.required ? '' : ' nil'}`)
            .join(', ');
          const hash = t.schema.map((f) => `${f.key}: ${f.key}`).join(', ');
          return `  def ${fnName}(recipient, ${kwArgs})\n    send_template(:${t.camelName}, recipient, { ${hash} })\n  end`;
        }
        return `  def ${fnName}(recipient, data = {})\n    send_template(:${t.camelName}, recipient, data)\n  end`;
      }),
    )
    .join("\n\n");

  return `# mail_service.rb — generated by Mail Service SDK Export
require 'net/http'
require 'json'
require 'uri'
require_relative 'mail_config'

class MailService
  def initialize
    @service_url = (ENV['${config.serviceUrlVarName}'] || '').chomp('/')
  end

  def send_template(template, recipient, data = {})
    t = MAIL_TEMPLATES[template]
    post(t[:app], '/v1/send', {
      template_slug: t[:slug],
      recipient:     recipient,
      data:          data,
    })
  end

  def send_raw(app, subject:, html:, recipient:, from_name: nil)
    post(app, '/v1/send/raw', { subject: subject, html: html, recipient: recipient, from_name: from_name })
  end

${typedMethods}

  private

  def get_key(app)
    ENV[MAIL_APPS[app][:key_env]] || ''
  end

  def ready?(app)
    !@service_url.empty? && !get_key(app).empty?
  end

  def post(app, path, body)
    return { success: false, error: 'not configured' } unless ready?(app)
    uri = URI("#{@service_url}#{path}")
    req = Net::HTTP::Post.new(uri, 'Content-Type' => 'application/json', 'X-API-KEY' => get_key(app))
    req.body = body.to_json
    resp = Net::HTTP.start(uri.host, uri.port, use_ssl: uri.scheme == 'https') { |h| h.request(req) }
    JSON.parse(resp.body)
  rescue => e
    { success: false, error: e.message }
  end
end

Mail = MailService.new
`;
}

export const rubyGenerator: LanguageGenerator = {
  id: 'ruby',
  name: 'Ruby',
  category: 'full-sdk',
  color: '#CC342D',
  generate(config: ExportConfig): GeneratedFile[] {
    return [
      { filename: 'mail_config.rb', content: genConfig(config), syntax: 'ruby' },
      { filename: 'mail_service.rb', content: genService(config), syntax: 'ruby' },
      { filename: '.env', content: generateEnv(config), syntax: 'shell' },
    ];
  },
};
